import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Navigation, Plus, Edit, Trash2, ToggleLeft, ToggleRight, Settings2, Info, Crown } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { navigationService, StoreNavigationMenu } from '../services/navigation.service'
import { NavigationMenuModal } from '../components/navigation/NavigationMenuModal'
import { NavigationItemsManager } from '../components/navigation/NavigationItemsManager'

export function NavigationMenus() {
  const [menus, setMenus] = useState<StoreNavigationMenu[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    perPage: 15,
    lastPage: 1,
    from: 0,
    to: 0,
  })

  // Modals
  const [showMenuModal, setShowMenuModal] = useState(false)
  const [editingMenuId, setEditingMenuId] = useState<number | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [statusConfirmMenu, setStatusConfirmMenu] = useState<StoreNavigationMenu | null>(null)
  const [managingMenu, setManagingMenu] = useState<StoreNavigationMenu | null>(null)

  const fetchMenus = async (page = pagination.currentPage, search = searchQuery) => {
    setIsLoading(true)
    try {
      const res = await navigationService.getMenus({ page, per_page: pagination.perPage, search })
      const paginator = res.data
      setMenus(paginator.data)
      setPagination({
        currentPage: paginator.current_page,
        total: paginator.total,
        perPage: paginator.per_page,
        lastPage: paginator.last_page,
        from: paginator.from,
        to: paginator.to,
      })
    } catch {
      toast.error('Error al cargar los menús')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMenus(pagination.currentPage, searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, searchQuery])

  // ── Abrir modal de creación ──────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingMenuId(null)
    setShowMenuModal(true)
  }

  // ── Abrir modal de edición ───────────────────────────────────────────────
  const openEditModal = (menu: StoreNavigationMenu) => {
    setEditingMenuId(menu.id)
    setShowMenuModal(true)
  }

  // ── Eliminar ─────────────────────────────────────────────────────────────
  const executeDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await navigationService.deleteMenu(deleteConfirmId)
      toast.success('Menú eliminado exitosamente')
      fetchMenus()
    } catch {
      toast.error('No se pudo eliminar el menú')
    } finally {
      setDeleteConfirmId(null)
    }
  }

  // ── Cambiar estado ────────────────────────────────────────────────────────
  const executeToggleStatus = async () => {
    if (!statusConfirmMenu) return
    try {
      await navigationService.toggleMenuStatus(statusConfirmMenu.id)
      toast.success(
        statusConfirmMenu.is_active
          ? 'Menú desactivado. La tienda queda sin menú de navegación.'
          : 'Menú activado como Menú del Header'
      )
      fetchMenus()
    } catch {
      toast.error('No se pudo actualizar el estado del menú')
    } finally {
      setStatusConfirmMenu(null)
    }
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Menús de Navegación</h1>
          <p className="mt-1 text-sm text-slate-600">
            Administra los menús y sus enlaces para la tienda en línea.
          </p>
        </div>
        <Button onClick={openCreateModal} className="w-full bg-primary hover:bg-primary/90 sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Menú
        </Button>
      </div>

      {/* Banner informativo */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
        <div>
          <p className="font-medium">Solo un menú puede estar activo a la vez</p>
          <p className="mt-0.5 text-blue-700">
            El menú activo es el que se muestra en el <strong>Header</strong> de la tienda en línea.
            Al activar un menú, el que estaba activo se desactivará automáticamente.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <GlobalFilterBar
        show={['search']}
        values={{ search: searchQuery }}
        onChange={(changes) => {
          if (changes.search !== undefined) {
            setSearchQuery(changes.search)
            setPagination((prev) => ({ ...prev, currentPage: 1 }))
          }
        }}
        customOptions={{ searchPlaceholder: 'Buscar menú...' }}
      />

      {/* Tabla */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Estado', 'Ítems', 'Acciones']}
          columns={['text', 'badge', 'badge', 'text', 'actions']}
          rows={6}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Ítems
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                {menus.length > 0 ? (
                  menus.map((menu) => (
                    <tr key={menu.id} className="hover:bg-table-row-hover transition-colors">
                      {/* Nombre */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 shrink-0">
                            <Navigation className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <p className="font-medium text-table-foreground">{menu.name}</p>
                            {menu.is_active && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 w-fit">
                                <Crown className="h-2.5 w-2.5" />
                                Menú del Header
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Código */}
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="bg-slate-100 font-mono text-xs">
                          {menu.code}
                        </Badge>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            menu.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {menu.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>

                      {/* Ítems */}
                      <td className="px-6 py-4 text-sm text-table-foreground">
                        {menu.items?.length ?? 0} enlace{(menu.items?.length ?? 0) !== 1 ? 's' : ''}
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Gestionar ítems */}
                          <button
                            onClick={() => setManagingMenu(menu)}
                            title="Gestionar enlaces"
                            className="rounded p-1.5 text-primary transition-colors hover:bg-primary/10"
                          >
                            <Settings2 className="h-4 w-4" />
                          </button>

                          {/* Toggle status */}
                          <button
                            onClick={() => setStatusConfirmMenu(menu)}
                            title={menu.is_active ? 'Desactivar' : 'Activar'}
                            className={`rounded p-1.5 transition-colors ${
                              menu.is_active
                                ? 'text-primary hover:bg-primary/10'
                                : 'text-table-foreground hover:bg-muted'
                            }`}
                          >
                            {menu.is_active ? (
                              <ToggleRight className="h-5 w-5" />
                            ) : (
                              <ToggleLeft className="h-5 w-5" />
                            )}
                          </button>

                          {/* Editar */}
                          <button
                            onClick={() => openEditModal(menu)}
                            title="Editar"
                            className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          {/* Eliminar */}
                          <button
                            onClick={() => setDeleteConfirmId(menu.id)}
                            title="Eliminar"
                            className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <Navigation className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron menús de navegación</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {menus.length > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
            />
          )}
        </div>
      )}

      {/* Modal crear/editar menú */}
      {showMenuModal && (
        <NavigationMenuModal
          open={showMenuModal}
          onOpenChange={(v) => {
            setShowMenuModal(v)
            if (!v) setEditingMenuId(null)
          }}
          menuId={editingMenuId}
          onSuccess={() => {
            fetchMenus()
            setShowMenuModal(false)
            setEditingMenuId(null)
          }}
        />
      )}

      {/* Manager de ítems */}
      {managingMenu && (
        <NavigationItemsManager
          open={!!managingMenu}
          onOpenChange={(v) => {
            if (!v) setManagingMenu(null)
          }}
          menuId={managingMenu.id}
          menuName={managingMenu.name}
        />
      )}

      {/* Confirmaciones */}
      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="menú"
      />
      {/* Toggle status: activar → advertencia de reemplazo */}
      {statusConfirmMenu && !statusConfirmMenu.is_active && (
        <ConfirmationModal
          isOpen
          onClose={() => setStatusConfirmMenu(null)}
          onConfirm={executeToggleStatus}
          preset="toggleStatus"
          description={`Al activar "${statusConfirmMenu.name}" como Menú del Header, el menú activo actual será desactivado automáticamente. Solo puede haber un menú activo a la vez.`}
          confirmText="Sí, activar"
        />
      )}
      {/* Toggle status: desactivar → advertencia de sin menú */}
      {statusConfirmMenu && statusConfirmMenu.is_active && (
        <ConfirmationModal
          isOpen
          onClose={() => setStatusConfirmMenu(null)}
          onConfirm={executeToggleStatus}
          preset="delete"
          description={`Si desactivas "${statusConfirmMenu.name}", la tienda en línea quedará sin menú de navegación visible hasta que actives otro.`}
          confirmText="Sí, desactivar"
        />
      )}
    </div>
  )
}
